import { registerNewBatch, deliverFiles } from "./batchActions";
import { stageFilesLocally, finalizeLocalBatch } from "./storageService";

/**
 * 🛠️ GENERIC FILE UPLOADER
 * Location: src/registered/upload/services/genericFileUploader.js
 */
export const GenericFileUploader = {
    /**
     * @param {Object} esgTreeData - The 'fullPot' array generated by TaxonomyReconstructor.
     * @param {Object} config - Destinaton and Type parameters.
     * @param {String} config.bucketName - Target S3 Bucket (e.g., "esg-staging-clean").
     * @param {String} config.fileName - The physical name for the S3 Object.
     * @param {Array}  config.labels - Metadata tags for version tracking.
     * @param {Number} config.batchType - The specific ID for the batch category (e.g., 1 for Taxonomy).
     */
    uploadGeneratedData: async (esgTreeData, { bucketName, fileName, labels = [], batchType }) => {
        const siteKey = process.env.REACT_APP_SITE_KEY || window?.activeWorkflow?.siteKey;

        console.group(`📡 UPLOADER_DEBUG: ${fileName}`);
        console.table({ siteKey, bucketName, batchType, labels: labels.join(', ') });

        if (!siteKey) {
            console.error("❌ [UPLOADER_ABORT]: siteKey is missing from environment.");
            console.groupEnd();
            throw new Error("Missing siteKey configuration.");
        }

        const blob = new Blob([JSON.stringify(esgTreeData)], { type: "application/json" });
        const virtualFile = new File([blob], fileName, { type: "application/json" });

        console.log(`📦 Payload Prepared: ${(blob.size / 1024).toFixed(2)} KB`);

        const metaData = {
            reportingYear: new Date().getFullYear(),
            versionLabel: labels.join(" | ") || "System Generated Sync",
            S3CorsValue: "esg",
            targetBucket: bucketName,
            targetRoot: fileName.split('.')[0],
            batchType: batchType
        };

        try {
            console.log(`🚀 DISPATCHING [Type: ${batchType}] TO ${bucketName}: ${fileName}`);

            const newBatch = await registerNewBatch(
                [virtualFile],
                { siteKey: siteKey, batchType: batchType },
                metaData
            );
            console.log("✅ [STEP A] BFF Handshake Success. Batch ID:", newBatch.batchId);

            await stageFilesLocally([virtualFile], newBatch.batchId, siteKey);
            console.log("✅ [STEP B] Local Staging (Dexie) Complete.");

            await finalizeLocalBatch(
                newBatch.batchId,
                siteKey,
                newBatch.hash,
                newBatch.timestamp,
                metaData
            );
            console.log("✅ [STEP C] Meta Finalized. Target Bucket in DB:", metaData.targetBucket);

            console.log("⏳ [STEP D] Triggering S3 Delivery Worker...");
            await deliverFiles(newBatch.batchId, [virtualFile], siteKey);

            console.log("🏆 UPLOAD COMPLETE.");
            console.groupEnd();

            return {
                success: true,
                batchId: newBatch.batchId,
                destination: bucketName
            };

        } catch (err) {
            console.error("🚨 GENERIC_UPLOADER_FAILURE", err);
            console.groupEnd();
            throw err;
        }
    }
};